import { Select, Spin } from "antd";
import type { SelectProps } from "antd/es/select";
import { getIndustryByType, getIndustryLeaderResult } from "api/search.api";

import debounce from "lodash/debounce";
import React, { useMemo, useRef, useState } from "react";

export interface DebounceSelectProps<ValueType = any>
  extends Omit<SelectProps<ValueType | ValueType[]>, "options" | "children"> {
  fetchOptions: (search: string, industryType: any) => Promise<ValueType[]>;
  industryType: any;
  debounceTimeout?: number;
}

function DebounceSelect<
  ValueType extends {
    key?: string;
    label: React.ReactNode;
    value: string | number;
  } = any
>({
  fetchOptions,
  industryType,
  debounceTimeout = 800,
  ...props
}: DebounceSelectProps<ValueType>) {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ValueType[]>([]);
  const fetchRef = useRef(0);

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string, industryType: any) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);

      fetchOptions(value, industryType).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          // for fetch callback order
          return;
        }

        setOptions(newOptions);
        setFetching(false);
      });
    };

    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);

  return (
    <Select
      labelInValue
      filterOption={false}
      onSearch={(e) => {
        debounceFetcher(e, industryType);
      }}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      {...props}
      options={options}
    />
  );
}

// Usage of DebounceSelect
interface UserValue {
  label: string;
  value: string;
}

async function fetchUserList(
  username: string,
  industryType: any
): Promise<UserValue[]> {
  console.log("fetching user", username);
  if (industryType && industryType.length) {
    return getIndustryByType({
      fortune_100: industryType.indexOf("fortune_100") > -1,
      fortune_500: industryType.indexOf("fortune_500") > -1,
      fortune_1000: industryType.indexOf("fortune_1000") > -1,

      text: username,
    }).then((data: any) => {
      return data?.data.autocompleteCompanyIndustry.matches.map(
        (label: any) => ({
          label: label.company,
          value: label.company,
          extra: label,
        })
      );
    });
  }
  return getIndustryLeaderResult(username).then((data: any) => {
    return data?.data.autocomplete.result.map((label: any) => ({
      label: label,
      value: label,
      extra: label,
    }));
  });
}

interface CompanyDropdownProps {
  className?: string;
  placeholder?: string;
  onClick?: any;
  onChange?: any;
  defaultValue?: any;
  valueSetterRequired?: any;
  industryType?: string[];
  [key: string]: any;
  styles?: any;
}
const IndustryLeaderDropdown: React.FC<CompanyDropdownProps> = ({
  className,
  placeholder,
  onClick,
  defaultValue,
  valueSetterRequired,
  onChange,
  industryType,
  styles,
  ...props
}) => {
  const [value, setValue] = useState<UserValue[]>(
    defaultValue ? defaultValue : []
  );
  valueSetterRequired =
    valueSetterRequired == true || valueSetterRequired == false
      ? valueSetterRequired
      : true;
  return (
    <DebounceSelect
      mode="multiple"
      maxTagCount={1}
      value={defaultValue}
      placeholder={placeholder}
      fetchOptions={fetchUserList}
      onChange={(newValue, ex) => {
        onChange && onChange(newValue, ex);
        setValue(newValue as UserValue[]);
        //if (valueSetterRequired) setValue(newValue as UserValue[]);
      }}
      onClick={onClick}
      style={styles ? styles : { width: "100%" }}
      className={className}
      industryType={industryType}
      {...props}
    />
  );
};

export default IndustryLeaderDropdown;
